import { EmployerHeader, EmployerFooter } from '@seek/adv-header-footer';
import { trackHeaderFooterInteractions } from '@seek/cmsu-analytics';
import type { Section } from '@seek/cmsu-cms-connect';
import { CustomPageHeader, CustomPageFooter } from '@seek/cmsu-components';
import { CMSUProvider } from '@seek/cmsu-components/src/hooks/useCMSUContext';
import type { SiteName, Language } from '@seek/melways-sites';
import { HELP_CENTRE_LOCALISED_URL } from '@seek/shared-constants';
import camelCase from 'camelcase';
import { useMemo } from 'react';

import { useAuth, useConfig } from 'src/client/shared/hooks';
import type { EmployerSiteName } from 'src/client/shared/types';
import {
  getFooterConfig,
  getHeaderConfig,
} from 'src/client/shared/utils/helper';

interface Props {
  children: React.ReactNode;
  section?: Partial<Section>;
  categoryName?: string;
}
type TabIdProps = Record<string, string>;

type ActiveTabID = NonNullable<
  React.ComponentProps<typeof EmployerHeader>['activeTabId']
>;

type ActiveSubTabId = NonNullable<
  React.ComponentProps<typeof EmployerHeader>['activeSubTabId']
>;

const getProductTabId: TabIdProps = {
  jobads: 'jobAds',
  adcentre: 'candidateManagement',
  talentsearch: 'talentSearchProduct',
  branding: 'branding',
  'company-profiles': 'companyProfiles',
  'find-a-recruiter': 'findARecruiter',
  products: 'whatsNew',
};

export const EmployerLayout = ({ children, section, categoryName }: Props) => {
  const {
    site,
    melwaysLocale,
    isStaging,
    language,
    routeArray,
    country,
    hygraphLocale,
  } = useConfig();
  const { hirerAccountContext } = useAuth();
  const { sectionName, headerOptions, footerOptions } = section || {};
  const parentSectionName = section?.parentSection?.sectionName;

  const { type, layout, logo, links, divider, linksAlignment } =
    getHeaderConfig(headerOptions);

  const footerConfig = getFooterConfig(footerOptions);

  const displayCustomFooter = footerConfig?.type === 'CustomFooter';

  /*
    TabId is used to highlight the active tab in the header, but requires a camelCase string.
    Currently the 'categoryName' prop is supplied using the page 'slug' - I don't know why the change in terminology
  */

  const activeSubTabId = useMemo(() => {
    /*
      Note: The `products` section categoryNames often are not hyphenated - or even
      change naming completely, so we need to convert them to camelCase manually
      The 'What's New' tab is the default tab for the 'products' section and doesn't
      have a page slug, so is provided as 'undefined'
    */
    if (sectionName === 'products') {
      return categoryName
        ? getProductTabId[categoryName] || 'blank'
        : 'whatsNew';
    }
    if (parentSectionName && sectionName) return camelCase(sectionName);
    return categoryName ? camelCase(categoryName) : 'none';
  }, [categoryName, parentSectionName, sectionName]);

  const activeTabId = useMemo(() => {
    if (parentSectionName) return camelCase(parentSectionName);
    return sectionName ? camelCase(sectionName) : 'none';
  }, [parentSectionName, sectionName]);

  const userIdentityProps = {
    advertiser: hirerAccountContext?.advertiser,
    identityContext: hirerAccountContext?.identityContext,
    user: hirerAccountContext?.user,
  };

  const renderFooter = () => {
    if (displayCustomFooter) {
      const {
        logo: footerLogo,
        links: footerLinks,
        backgroundColor: footerBackgroundColor,
      } = footerConfig;
      return (
        <CMSUProvider config={{ language, country }}>
          <CustomPageFooter
            country={country}
            locale={hygraphLocale}
            backgroundColor={footerBackgroundColor}
            logo={footerLogo}
            links={footerLinks}
            language={language}
          />
        </CMSUProvider>
      );
    }
    return (
      <EmployerFooter
        isStaging={isStaging}
        locale={melwaysLocale}
        site={site as EmployerSiteName}
        localisedContactInfo={{
          helpCentreLink:
            HELP_CENTRE_LOCALISED_URL[
              site as Exclude<
                SiteName,
                | 'candidate-seek-root'
                | 'candidate-jobsdb-root'
                | 'candidate-jobstreet-root'
              >
            ][language as Language],
        }}
        {...userIdentityProps}
      />
    );
  };
  return (
    <>
      {/*
        Note: The header and footer relies on the `window` object to get URLs for the language selector which causes hydration issues since `window` is undefined on the server.
        @ref https://github.com/SEEK-Jobs/metropolis/blob/master/packages/anz-employer-header-footer/src/EmployerHeader/components/LanguageSelector/getWindowPathWithoutLanguage.ts
      */}
      {type === 'SeekHeader' && layout !== 'none' ? (
        <EmployerHeader
          activeSubTabId={activeSubTabId as ActiveSubTabId}
          activeTabId={activeTabId as ActiveTabID}
          basic={layout === 'basic'}
          isStaging={isStaging}
          locale={melwaysLocale}
          site={site as EmployerSiteName}
          onTrackedItemInteraction={trackHeaderFooterInteractions}
          {...(layout !== 'basic' && userIdentityProps)}
        />
      ) : (
        <CustomPageHeader
          logo={logo}
          links={links}
          site={site}
          language={language}
          route={routeArray}
          country={country}
          locale={hygraphLocale}
          divider={divider}
          linksAlignment={linksAlignment}
        />
      )}
      {children}
      {renderFooter()}
    </>
  );
};
